  import { useEffect, useState } from 'react';
  import { Navigate, useLocation, useNavigate } from 'react-router-dom';

  import { CircularProgress, Stack } from '@mui/material';
  import { checkTokenValidity } from '../../common/utils/checkTokenValidity';
  import { ROUTES } from '../../constants/routes';
  import { localStorageService } from '../../services/localStorageService/localStorageService';
  import { sendEventToReactNative } from '../../common/utils/reactNativeHelpers';
  import { WebViewEvents } from '../../common/enums/webViewEvents';
  import { useUserActions } from '../../hooks/useUser';
  import { authService } from '../../services/auth/AuthService';

  const PrivateRoute = ({ children }: { children: React.ReactNode }) => {
    const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
    const [token, setToken] = useState<string | null>(null);
    const [isTokenLoaded, setIsTokenLoaded] = useState<boolean>(false);
    const location = useLocation();

    const { setAccessToDashboardCallback } = useUserActions();
    const navigate = useNavigate();

    const checkAuth = async () => {
      const response = await authService.checkPermission();
      setAccessToDashboardCallback(response);
    };

    useEffect(() => {
      const cookies = document.cookie
        .split('; ')
        .find((row) => row.startsWith('user='))
        ?.split('=')[1];

      checkAuth();

      if (cookies) {
        const userCredentials = JSON.parse(decodeURIComponent(cookies));
        localStorageService().setAccessToken(userCredentials.accessToken);
        localStorageService().setRefreshToken(userCredentials.refreshToken);
        localStorageService().setUserId(userCredentials.id);
        setToken(userCredentials.accessToken);
        setIsTokenLoaded(true);
        sendEventToReactNative(WebViewEvents.SUCCESS_AUTH);
        
        // Check for saved redirect URL after successful Google/OAuth authentication
        const redirectUrl = localStorageService().getRedirectUrl();
        if (redirectUrl && location.pathname !== redirectUrl) {
          localStorageService().removeRedirectUrl();
          navigate(redirectUrl);
        }
      } else {
        setIsTokenLoaded(true);
      }
    }, []);

    useEffect(() => {
      if (isTokenLoaded) {
        if (token) {
          checkTokenValidity(token).then((isValid) => {
            if (!isValid) {
              localStorageService().removeAccessToken();
              localStorageService().removeRefreshToken();
            }
            setIsAuthenticated(isValid);
          });
        } else {
          const tokenFromStorage = localStorageService().getAccessToken();
          if (tokenFromStorage)
            checkTokenValidity(tokenFromStorage).then((isValid) => {
              if (!isValid) {
                localStorageService().removeAccessToken();
                localStorageService().removeRefreshToken();
              }
              setIsAuthenticated(isValid);
            });
          else setIsAuthenticated(false);
        }
      }
    }, [token, isTokenLoaded]);

    if (isAuthenticated === null) {
      return (
        <Stack sx={{ margin: 'auto' }}>
          <CircularProgress />
        </Stack>
      );
    }

    if (!isAuthenticated) {
      // Save the current URL to localStorage before redirecting to login
      const currentPath = location.pathname + location.search;
      if (currentPath !== `/${ROUTES.LOGIN}`) {
        localStorageService().setRedirectUrl(currentPath);
      }
      return <Navigate to={`/${ROUTES.LOGIN}`} />;
    }

    if (window.location.pathname === '/') {
      return <Navigate to={`/${ROUTES.CABINET}`} />;
    }
    return <>{children}</>;
  };

  export default PrivateRoute;
